import { set } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { DeepPartial } from "redux";
import { Option } from "space-monad";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { countries } from "../../../store/common/models";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { LaunchesActions } from "../../../store/launches/actions";
import { LaunchesDTO } from "../../../store/launches/models/dto";
import { Users } from "../../../store/users/reducer";
import { ConfigContext } from "./config-context";

/**
 *
 */
export function useRunInitialValues(
  item: LaunchConfigurationsDTO.Responses.GetSingle.T,
  config: ConfigContext["state"]
) {
  const dispatch = useDispatch();
  const userDetails = useFetchable(Users.current);

  const locatedIn = useMemo(() => {
    return Option(config.fingerprint)
      .map((x) => x.ipLocation?.country)
      .map((x) => countries.keys.find((y) => y === x.code))
      .get();
  }, [config.fingerprint, countries]);

  const ipAddress = useMemo(() => {
    return Option(config.fingerprint)
      .map((x) => x.ip)
      .get();
  }, [config.fingerprint]);

  const isVerified = useMemo(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    return (
      isFetchableSuccess(userDetails) &&
      (!urlSearchParams.has("user") ||
        urlSearchParams.get("user") === userDetails.data.emailAddress) &&
      userDetails.data.kycStatus === "VERIFIED"
    );
  }, [userDetails, window.location.search]);

  useEffect(() => {
    if (isFetchableSuccess(userDetails)) {
      dispatch(
        LaunchesActions.setParticipationForm({
          id: item.id,
          data: {
            user: {
              emailAddress: userDetails.data.emailAddress,
            },
          },
        })
      );
    }
  }, [userDetails, dispatch]);

  // state for readiness
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (!ready && locatedIn) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const data: DeepPartial<LaunchesDTO.Requests.ParticipationForm.ForForm> = {
        // set policies and regulations to false only if found in config
        policies: {
          sekuritanceStatements: item.pages.find((x) =>
            x.fields.find((y) => y.field === "policies.sekuritanceStatements")
          )
            ? false
            : undefined,
          finalDeclaration: item.pages.find((x) =>
            x.fields.find((y) => y.field === "policies.finalDeclaration")
          )
            ? false
            : undefined,
          declarationStatements: item.pages.find((x) =>
            x.fields.find((y) => y.field === "policies.declarationStatements")
          )
            ? false
            : undefined,
          whitepaperRead: item.pages.find((x) =>
            x.fields.find((y) => y.field === "policies.whitepaperRead")
          )
            ? false
            : undefined,
        },
        regulations: {
          pep: item.pages.find((x) =>
            x.fields.find((y) => y.field === "regulations.pep")
          )
            ? false
            : undefined,
          resideSanctionedCountry: item.pages.find((x) =>
            x.fields.find(
              (y) => y.field === "regulations.resideSanctionedCountry"
            )
          )
            ? false
            : undefined,
          usPerson: item.pages.find((x) =>
            x.fields.find((y) => y.field === "regulations.usPerson")
          )
            ? false
            : undefined,
        },
        user: {
          physicallyLocatedIn: locatedIn,
        },
        device: {
          ipAddress,
          ipCountryCode: locatedIn,
        },
      };

      if (!isVerified && urlSearchParams.has("firstName")) {
        set(data, "user.names.first", urlSearchParams.get("firstName"));
      }

      if (!isVerified && urlSearchParams.has("lastName")) {
        set(data, "user.names.last", urlSearchParams.get("lastName"));
      }

      if (!isVerified && urlSearchParams.has("dob")) {
        set(data, "user.dob", urlSearchParams.get("dob"));
      }

      if (urlSearchParams.has("user")) {
        set(data, "user.emailAddress", urlSearchParams.get("user"));
      }

      if (!isVerified && urlSearchParams.has("nationality")) {
        set(data, "user.nationality", urlSearchParams.get("nationality"));
      }

      if (urlSearchParams.has("address") && urlSearchParams.has("chain")) {
        set(data, "sourceWallet.address", urlSearchParams.get("address"));
        set(data, "sourceWallet.chain", urlSearchParams.get("chain"));
      }

      dispatch(
        LaunchesActions.setParticipationForm({
          id: item.id,
          data,
        })
      );
      setReady(true);
    }
  }, [item, isVerified, setReady, dispatch, ready, locatedIn, ipAddress]);

  return ready;
}
